import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { usePlatform, useRunTimeEnvironment, useScript } from '../../utils/hooks';
import { runAdmob, useAdsListener } from '../../services/admob';

const PENDING_STORAGE_KEY = 'attendance-pending-claim';
const PENDING_TTL_MS = 180000;
const ERROR_GRACE_MS = 400;
const AD_HANDOFF_MS = 900;

function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function ensureNotifyAds() {
  if (typeof window.notifyAds === 'function') {
    return;
  }
  window.notifyAds = (event, reward) => {
    window.dispatchEvent(
      new CustomEvent('ads', {
        detail: { event, reward },
      }),
    );
  };
}

function writePending(pending) {
  try {
    sessionStorage.setItem(
      PENDING_STORAGE_KEY,
      JSON.stringify({ ...pending, ts: Date.now() }),
    );
  } catch (error) {
    // private mode 등
  }
}

function clearStoredPending() {
  try {
    sessionStorage.removeItem(PENDING_STORAGE_KEY);
  } catch (error) {
    // no-op
  }
}

function readStoredPending() {
  try {
    const raw = sessionStorage.getItem(PENDING_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed?.code) {
      return null;
    }
    if (Date.now() - (parsed.ts || 0) > PENDING_TTL_MS) {
      sessionStorage.removeItem(PENDING_STORAGE_KEY);
      return null;
    }
    return { code: parsed.code, step: parsed.step };
  } catch (error) {
    return null;
  }
}

export function useAttendanceClaim({ data, mutate, onToast }) {
  const RTE = useRunTimeEnvironment();
  const browserDeviceType = usePlatform();
  const { API_SERVER_DOMAIN, useAccessToken, appInfo } = useContext(AuthContext);
  const accessToken = useAccessToken();
  const deviceType = appInfo?.os === 'ios' || appInfo?.os === 'android' ? appInfo.os : browserDeviceType;
  const pendingRef = useRef(null);
  const errorTimerRef = useRef(null);
  const claimLockRef = useRef(false);
  const ignoreAdEventUntilRef = useRef(0);
  const adShownAtRef = useRef(0);
  const [busy, setBusy] = useState(false);

  useScript(`${process.env.PUBLIC_URL}/flutter/flutter_script.js`);

  useEffect(() => {
    ensureNotifyAds();
    const stored = readStoredPending();
    if (stored && !pendingRef.current) {
      pendingRef.current = stored;
      setBusy(true);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (errorTimerRef.current) {
        window.clearTimeout(errorTimerRef.current);
      }
    };
  }, []);

  const setPending = useCallback((pending) => {
    pendingRef.current = pending;
    if (pending?.code) {
      writePending(pending);
    } else {
      clearStoredPending();
    }
  }, []);

  const takePending = useCallback(() => {
    const pending = pendingRef.current?.code ? pendingRef.current : readStoredPending();
    if (!pending?.code) {
      return null;
    }
    pendingRef.current = null;
    clearStoredPending();
    return pending;
  }, []);

  const handleAdError = useCallback(
    (errorInfo = {}) => {
      if (Date.now() < ignoreAdEventUntilRef.current) {
        return;
      }
      if (!pendingRef.current?.code && !readStoredPending()) {
        return;
      }
      const pendingAtError = pendingRef.current?.code || readStoredPending()?.code;
      if (errorTimerRef.current) {
        window.clearTimeout(errorTimerRef.current);
      }
      errorTimerRef.current = window.setTimeout(() => {
        const currentCode = pendingRef.current?.code || readStoredPending()?.code;
        if (!currentCode || currentCode !== pendingAtError) {
          return;
        }
        const watchedMs = Date.now() - (adShownAtRef.current || 0);
        if (watchedMs < 3500) {
          setPending(null);
          setBusy(false);
          const message =
            typeof errorInfo?.message === 'string' && errorInfo.message.trim().length > 0
              ? errorInfo.message
              : '광고를 끝까지 봐야 보상이 지급됩니다.';
          onToast?.(message);
          return;
        }
        onAdEndRef.current();
      }, ERROR_GRACE_MS);
    },
    [onToast, setPending],
  );

  const postClaim = useCallback(
    async (code) => {
      const response = await fetch(`${API_SERVER_DOMAIN}/main/attendance/claim`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });
      return response.json();
    },
    [API_SERVER_DOMAIN, accessToken],
  );

  const startAd = useCallback(
    async (step) => {
      if (RTE !== 'APP') {
        onToast?.('광고 시청은 앱에서만 가능해요');
        setBusy(false);
        return false;
      }
      if (typeof window.flutter_inappwebview?.callHandler !== 'function') {
        onToast?.('광고 재생에 실패했어요.');
        setBusy(false);
        return false;
      }
      ensureNotifyAds();
      setBusy(true);
      try {
        const response = await fetch(`${API_SERVER_DOMAIN}/main/attendance/ad/start`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ step, deviceType }),
        });
        const json = await response.json();
        if (json?.result !== 'ok') {
          onToast?.(json?.message || '광고를 불러오지 못했어요.');
          setBusy(false);
          return false;
        }
        // 디버깅용
        // onToast?.(json.placementId || '광고코드 없음.');
        // await wait(2000);

        setPending({ code: json.code, step: json.step });
        ignoreAdEventUntilRef.current = Date.now() + AD_HANDOFF_MS;
        adShownAtRef.current = Date.now();
        runAdmob(json.adType ?? '', json.placementId ?? '');
        return true;
      } catch (error) {
        console.error('attendance ad start error', error);
        setPending(null);
        onToast?.('광고를 불러오지 못했어요.');
        setBusy(false);
        return false;
      }
    },
    [API_SERVER_DOMAIN, RTE, accessToken, deviceType, onToast, setPending],
  );

  const onAdEndRef = useRef(() => {});
  onAdEndRef.current = async () => {
    if (Date.now() < ignoreAdEventUntilRef.current) {
      return;
    }
    if (claimLockRef.current) {
      return;
    }
    if (errorTimerRef.current) {
      window.clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
    const pending = takePending();
    if (!pending?.code) {
      return;
    }
    claimLockRef.current = true;
    try {
      const json = await postClaim(pending.code);
      if (json?.result !== 'ok') {
        onToast?.(json?.message || '출석 처리에 실패했어요.');
        setBusy(false);
        await mutate?.();
        return;
      }
      if (json.step === 'CATCHUP') {
        onToast?.('이어하기 광고 시청 완료');
        // 10초 전면이 닫힌 직후 바로 mutate하면 WebView가 흰 화면으로 남는 경우가 있음
        await wait(400);
        await mutate?.();
        setBusy(false);
        return;
      }
      onToast?.(json.rewardText || '출석 완료!');
      await mutate?.();
      setBusy(false);
    } catch (error) {
      console.error('attendance claim error', error);
      onToast?.('출석 처리에 실패했어요.');
      setBusy(false);
    } finally {
      claimLockRef.current = false;
    }
  };

  const listenerParams = useMemo(
    () => ({ task: 'attendance', onError: handleAdError }),
    [handleAdError],
  );

  useAdsListener('', '', listenerParams, () => {
    onAdEndRef.current();
  });

  const onCta = useCallback(() => {
    if (!data || data.ctaType === 'DONE' || busy) {
      return;
    }
    const step = data.ctaType === 'CATCHUP' ? 'CATCHUP' : 'REWARD';
    startAd(step);
  }, [busy, data, startAd]);

  return { onCta, busy };
}
